import { useState, useEffect, useCallback } from "react";
import { Search, Loader2, ClipboardList, ListChecks, FileText, UserRound } from "lucide-react";
import axios from "axios";
import PatientDetail from "@/components/admin/PatientDetail";
import { STATUS_BADGE, STATUS_LABEL, PLAN_LABEL, formatDate } from "@/components/admin/patientConstants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PatientsManager = ({ exercises, subscribers, onRefresh }) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/patients`, { withCredentials: true });
      setPatients(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const filtered = patients.filter((p) => {
    const q = search.toLowerCase();
    const matches = !q || p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q);
    const active = !onlyActive || p.subscription_status === "active";
    return matches && active;
  });

  if (selected) {
    return (
      <PatientDetail
        patient={selected}
        exercises={exercises}
        subscribers={subscribers}
        onBack={() => setSelected(null)}
        onListChanged={() => {
          fetchPatients();
          onRefresh();
        }}
      />
    );
  }

  return (
    <div data-testid="patients-manager">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h2 className="font-serif text-xl text-stone-900">Gestione Pazienti ({filtered.length})</h2>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome o email..."
              className="pl-10 pr-4 py-2.5 rounded-full border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#2c4c3b]/20 text-sm w-full sm:w-64"
              data-testid="patients-search-input"
            />
          </div>
          <button
            onClick={() => setOnlyActive(!onlyActive)}
            className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
              onlyActive ? "bg-[#2c4c3b] text-stone-50" : "bg-white text-stone-600 border border-stone-200 hover:bg-stone-50"
            }`}
            data-testid="patients-active-filter"
          >
            Solo abbonati attivi
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#2c4c3b]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-stone-100">
          <UserRound className="w-12 h-12 text-stone-200 mx-auto mb-4" />
          <p className="text-stone-500">Nessun paziente trovato</p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-stone-100 overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-[#f5f2ec] text-[11px] uppercase tracking-wider text-stone-500 font-medium">
            <span className="col-span-3">Nome</span>
            <span className="col-span-4">Email</span>
            <span className="col-span-2">Pagamento</span>
            <span className="col-span-2">Iscrizione</span>
            <span className="col-span-1"></span>
          </div>
          {filtered.map((p) => (
            <button
              key={p.email}
              onClick={() => setSelected(p)}
              className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-4 px-6 py-4 border-t border-stone-100 hover:bg-[#f7faf6] transition-colors items-center"
              data-testid={`patient-row-${p.email}`}
            >
              <span className="col-span-3 font-medium text-stone-900">{p.name || "—"}</span>
              <span className="col-span-4 text-sm text-stone-500 truncate">{p.email}</span>
              <span className="col-span-2">
                <span className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${STATUS_BADGE[p.subscription_status] || STATUS_BADGE.none}`}>
                  {STATUS_LABEL[p.subscription_status] || p.subscription_status}
                </span>
                {p.plan_id && <span className="ml-1.5 text-[11px] text-stone-400">{PLAN_LABEL[p.plan_id] || p.plan_id}</span>}
              </span>
              <span className="col-span-2 text-sm text-stone-500">{formatDate(p.registered_at)}</span>
              <span className="col-span-1 flex gap-1.5 justify-start md:justify-end text-stone-300">
                {p.has_questionnaire && <ClipboardList className="w-4 h-4 text-[#8a9a86]" />}
                {p.has_program && <ListChecks className="w-4 h-4 text-[#8a9a86]" />}
                {p.letters_count > 0 && <FileText className="w-4 h-4 text-[#8a9a86]" />}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PatientsManager;
